import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { School, FileText, ArrowRight, Building } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    school_name: '',
    udise_code: '',
    cluster: '',
    block: '',
    district: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate UDISE code - must be exactly 11 digits
    if (!/^\d{11}$/.test(formData.udise_code)) {
      toast.error('UDISE Code must be exactly 11 digits');
      return;
    }

    // Validate mobile number - must be exactly 10 digits
    if (!/^\d{10}$/.test(formData.mobile)) {
      toast.error('Mobile number must be exactly 10 digits');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      await authAPI.register(registerData);
      
      toast.success('Registration successful! You can now login.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center bg-navy relative overflow-hidden p-12">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/5548399/pexels-photo-5548399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 text-center">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
            <School className="w-14 h-14 text-navy" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-4">
            Teacher Utility Toolkit
          </h1>
          <p className="text-slate-300 text-lg mb-8">
            Smart Tools for Smart Teachers
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80">
            <FileText className="w-5 h-5" />
            <span>Trusted by schools across Odisha</span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-navy text-xl">Teacher Utility Toolkit</span>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="font-heading text-2xl text-navy">Create Your Account</CardTitle>
              <CardDescription>
                Register your school to access the LO Format Generator
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="school_name">School Name</Label>
                  <Input
                    id="school_name"
                    name="school_name"
                    placeholder="Enter full school name"
                    value={formData.school_name}
                    onChange={handleChange}
                    required
                    data-testid="register-school-name"
                  />
                </div>

                <div>
                  <Label htmlFor="udise_code">UDISE Code (11 digits)</Label>
                  <Input
                    id="udise_code"
                    name="udise_code"
                    placeholder="Enter 11-digit UDISE code"
                    value={formData.udise_code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setFormData({ ...formData, udise_code: val });
                    }}
                    required
                    maxLength={11}
                    inputMode="numeric"
                    pattern="\d{11}"
                    data-testid="register-udise"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.udise_code.length}/11 digits
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cluster">Cluster</Label>
                    <Input
                      id="cluster"
                      name="cluster"
                      placeholder="Cluster name"
                      value={formData.cluster}
                      onChange={handleChange}
                      required
                      data-testid="register-cluster"
                    />
                  </div>
                  <div>
                    <Label htmlFor="block">Block</Label>
                    <Input
                      id="block"
                      name="block"
                      placeholder="Block name"
                      value={formData.block}
                      onChange={handleChange}
                      required
                      data-testid="register-block"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="district">District</Label>
                  <Input
                    id="district"
                    name="district"
                    placeholder="District name"
                    value={formData.district}
                    onChange={handleChange}
                    required
                    data-testid="register-district"
                  />
                </div>

                <div>
                  <Label htmlFor="mobile">Mobile Number (10 digits)</Label>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, mobile: val });
                    }}
                    required
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    data-testid="register-mobile"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.mobile.length}/10 digits
                  </p>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    data-testid="register-password"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-amber hover:bg-amber-dark text-white py-6 text-lg font-semibold"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Register & Pay ₹199'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Already have an account?{' '}
                  <Link to="/login" className="text-navy hover:underline font-medium">
                    Login here
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
